/**
 * The grant dialog's optional expiry (design D19).
 *
 * `expires_at` is normalized to naive UTC for storage, and the shipped
 * `expires_at Timezone Normalization` requirement reads an **offset-less**
 * string as already being UTC. A datetime picker hands back the operator's
 * local wall clock, so sending those digits unconverted would store an expiry
 * shifted by the operator's UTC offset — eight hours late for a `UTC+08:00`
 * operator — with no error anywhere: the value is well-formed, in the future,
 * and accepted.
 *
 * So the console converts: picked in local time, serialized as the equivalent
 * UTC instant with an explicit `Z`, and displayed back in local time so what is
 * shown is what was chosen.
 */

export interface LocalWallClock {
  year: number
  month: number // 1-12
  day: number
  hour: number
  minute: number
  second: number
}

const LOCAL_INPUT = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/

/** Parse the `datetime-local` spelling `YYYY-MM-DDTHH:mm[:ss]`. */
export function parseLocalDateTimeInput(raw: string): LocalWallClock | null {
  const m = LOCAL_INPUT.exec(raw.trim())
  if (!m) return null
  return {
    year: Number(m[1]),
    month: Number(m[2]),
    day: Number(m[3]),
    hour: Number(m[4]),
    minute: Number(m[5]),
    second: m[6] ? Number(m[6]) : 0,
  }
}

/**
 * The browser's UTC offset **in minutes east of UTC** for a given wall clock
 * (`UTC+08:00` → `480`). Taken from the instant itself rather than from "now",
 * so a selection across a DST boundary uses that date's own offset.
 */
export function localOffsetMinutes(local: LocalWallClock): number {
  // `new Date(y, m, d, ...)` interprets the parts as local wall clock, and
  // getTimezoneOffset() reports minutes *behind* UTC — hence the negation.
  const asLocal = new Date(
    local.year, local.month - 1, local.day, local.hour, local.minute, local.second
  )
  return -asLocal.getTimezoneOffset()
}

export interface ExpirySerialized {
  ok: true
  /** RFC 3339 with an explicit `Z`, no milliseconds. */
  value: string
  /** Epoch milliseconds of the chosen instant, for the future check. */
  epochMs: number
}

export interface ExpiryRejected {
  ok: false
  code: 'unparseable' | 'not_future'
  message: string
}

/**
 * Serialize a locally picked expiry into an explicit-`Z` UTC instant.
 *
 * `offsetMinutes` defaults to the browser's own offset for that wall clock; it
 * is injectable so the conversion can be asserted deterministically for a
 * non-UTC operator without depending on the test runner's time zone.
 */
export function serializeExpiry(
  raw: string,
  options: { now?: number; offsetMinutes?: number } = {}
): ExpirySerialized | ExpiryRejected {
  const local = parseLocalDateTimeInput(raw)
  if (!local) {
    return {
      ok: false,
      code: 'unparseable',
      message: 'Expiry must be a date and time.',
    }
  }
  const offset =
    options.offsetMinutes !== undefined ? options.offsetMinutes : localOffsetMinutes(local)
  const epochMs =
    Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute, local.second) -
    offset * 60_000
  const now = options.now ?? Date.now()
  if (epochMs <= now) {
    return {
      ok: false,
      code: 'not_future',
      message: 'Expiry must be later than the current time.',
    }
  }
  return { ok: true, value: toUtcInstantString(epochMs), epochMs }
}

/** `2026-09-14T16:00:00Z` — explicit designator, no milliseconds. */
export function toUtcInstantString(epochMs: number): string {
  return new Date(epochMs).toISOString().replace(/\.\d{3}Z$/, 'Z')
}

/**
 * Render a stored/serialized instant back in the operator's local time, so the
 * dialog shows the wall clock that was chosen rather than the UTC digits sent.
 */
export function formatExpiryLocal(
  iso: string,
  options: { offsetMinutes?: number } = {}
): string {
  const epochMs = Date.parse(iso)
  if (Number.isNaN(epochMs)) return iso
  const offset =
    options.offsetMinutes !== undefined
      ? options.offsetMinutes
      : -new Date(epochMs).getTimezoneOffset()
  const shifted = new Date(epochMs + offset * 60_000)
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}` +
    ` ${pad(shifted.getUTCHours())}:${pad(shifted.getUTCMinutes())}`
  )
}
